import isNil from 'lodash/isNil';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, post, put } from 'utils/api';
import { logError } from 'utils/log';
import { INSTAGRAM } from 'modules/insights/sov/utils/sov';
import {
  getConfigOptions,
  getEndpointConfig,
  setAuthInfo
} from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';

import type { Action, Dispatch, ThunkAction } from 'types';
import type {
  SOVReport,
  SOVReportType
} from 'modules/insights/sov/types';
import type {
  SOVGeneralAuthInfo,
  SOVReportState
} from 'modules/insights/sov/types/consumer-insights';

const { CI } = API.END_POINTS;

const REPORTS_PROCESSING = 'sov-ci/REPORTS_PROCESSING';
const REPORT_LOADED = 'sov-ci/REPORT_LOADED';
const REPORT_UPDATED = 'sov/REPORT_UPDATED';
const REPORT_RESET = 'sov-ci/REPORT_RESET';
const REPORT_TOKEN_SET = 'sov-ci/REPORT_TOKEN_SET';

const initialState = {
  report: {},
  hasToken: false,
  isLoading: false
};

const setStatus = (status: boolean): Action => ({
  type: REPORTS_PROCESSING,

  payload: {
    status
  }
});

const setTokenStatus = (status: boolean): Action => ({
  type: REPORTS_PROCESSING,

  payload: {
    status
  }
});

const reportLoaded = (report: SOVReport): Action => ({
  type: REPORT_LOADED,

  payload: {
    report
  }
});

const reportUpdated = (report: SOVReport): Action => ({
  type: REPORT_UPDATED,

  payload: {
    report
  }
});

export const resetReport = (): Action => ({
  type: REPORT_RESET
});

export const fetchReport = (
  authInfo: Partial<SOVGeneralAuthInfo>,
  reportType: SOVReportType = INSTAGRAM,
  contactEmail?: string | undefined,
  contactName?: string | undefined
): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));

  const configOptions = getConfigOptions(authInfo);
  const config = {
    ...configOptions.config,
    data: {
      reportType,
      ...(!isNil(contactEmail) ? { contactEmail } : null),
      ...(!isNil(contactName) ? { contactName } : null)
    }
  };

  // POST is used to create the report but BE is set up to check if the report already exists.
  // BE then determines whether to create the report or simply return a previously created one.
  try {
    const url = CI.SOV.getReport();
    const response = await post(url, config, configOptions.options);
    const report = response.data || [];

    setAuthInfo(report.id, authInfo);

    dispatch(setTokenStatus(true));
    dispatch(setStatus(false));

    return dispatch(reportLoaded(report));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

export const updateReport = (report: SOVReport): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setStatus(true));

  const reportId = report.id;

  try {
    const url = CI.SOV.updateReport(reportId, report.brandProfileId);
    const configOptions = getEndpointConfig(reportId, report);
    const response = await put(url, configOptions.config, configOptions.options);
    const updatedReport = response.data || {};

    dispatch(setStatus(false));

    return dispatch(reportUpdated(updatedReport));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

const handleProcessing = (state: SOVReportState, { status }): SOVReportState => ({
  ...state,
  isLoading: status
});

const handleSetToken = (state: SOVReportState, { status }): SOVReportState => ({
  ...state,
  hasToken: status
});

const handleLoadedReport = (state: SOVReportState, { report }): SOVReportState => ({
  ...state,
  report,
  hasToken: true,
  isLoading: false
});

const handleUpdated = (state: SOVReportState, { report }): SOVReportState => ({
  ...state,
  report,
  isLoading: false
});

const handleResetReport = (state: SOVReportState): SOVReportState => ({
  ...state,
  report: initialState.report,
  hasToken: false,
  isLoading: false
});

export default createReducer<SOVReportState, any>('sovReportsConsumerInsights', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [REPORTS_PROCESSING]: handleProcessing,
  [REPORT_LOADED]: handleLoadedReport,
  [REPORT_UPDATED]: handleUpdated,
  [REPORT_TOKEN_SET]: handleSetToken,
  [REPORT_RESET]: handleResetReport
});
